/**
 * Phase 5.5 — Due Out split / merge validation helper.
 *
 * Doctrine:
 *   - Bucket 4   Ticket splitting logic + job-line splits + manual merge +
 *                cross-split sync.
 *   - Bucket 4.5 §4.5.1  Due Out is a hard customer-promise constraint.
 *   - Bucket 4.5 §4.5.4  Split creation must prove final downstream completion <= Due Out.
 *   - Bucket 4.5 §4.5.5  Due Out outranks Waiter Flex; physical constraints
 *                        outrank Due Out.
 *   - Bucket 4.5 §4.5.7  Due Out changes are authorized + audited (Phase 5.1).
 *   - Bucket 4.5 §4.5.9  Waiter Flex must NOT extend Due Out.
 *   - Bucket 3 §3.4      Production states drive lifecycle semantics.
 *
 * Why this module exists
 * ----------------------
 * Existing code coverage at survey time:
 *
 *   - `supabase/functions/execute-manual-split/index.ts:467-472` throws
 *     when a single split part's `optiFinishDate` exceeds the parent
 *     `due_out` (RULEBOOK FIX 4). PARTIAL coverage — per-part check,
 *     no pre-flight projection helper, no formal vocabulary.
 *   - `supabase/functions/split-ticket/index.ts` inherits `due_out` to
 *     each child without an explicit Due Out check.
 *   - `src/utils/ticketMerging.ts:mergeSplitTickets` performs the only
 *     merge path: combines all sibling jobs into a fresh `smart-assign`
 *     call and soft-deletes the originals. NO Due Out check, NO
 *     completed-vs-incomplete preservation check.
 *   - NO partial-completion merge code path exists today.
 *
 * Phase 5.5 supplies the canonical vocabulary + validators so:
 *   - Split creation can adopt a single pre-flight projection helper
 *     instead of relying on a per-part throw inside the inner loop.
 *   - Manual merge gains a pre-flight gate that recomputes Due Out and
 *     blocks if remaining work would be hidden or completed work would
 *     be silently reopened.
 *   - A future partial-completion merge code path has a ready-made
 *     guard that pins the doctrinal preservation rules.
 *
 * Phase 5.5 scope: HELPER + TESTS ONLY.
 *   - Pure. No DB I/O, no clock reads, no React hooks. Caller passes
 *     `now`.
 *   - No DB migration, no schema change, no runtime mutation wiring.
 *   - The existing `execute-manual-split` / `split-ticket` /
 *     `ticketMerging` code paths are UNTOUCHED. Adoption points are
 *     documented at the bottom of this file.
 *
 * Builds on:
 *   - Phase 1.1 `getProductionState` (terminal-state exclusion).
 *   - Phase 1.4 `canPerformActiveProductionMutation` (mutation gate).
 *   - Phase 5.4 `getDueOutRiskExclusionReason` (eligibility short-circuit).
 *   - Phase 5.1 `buildDueOutEditAuditPayload` (consumers that re-promise
 *     after a failed split/merge route through this canonical writer).
 */

import {
  getProductionState,
  canPerformActiveProductionMutation,
  type ProductionState,
  type ProductionStateTicketShape,
} from './ticketProductionState';
import { getDueOutRiskExclusionReason } from './dueOutRiskEligibility';

// =============================================================================
// Type model
// =============================================================================

/**
 * Discrete blocker codes the helper emits. Machine-readable so UI /
 * resolver / audit log can route each blocker without parsing strings.
 */
export type SplitMergeBlocker =
  | 'final_completion_after_due_out'
  | 'missing_due_out'
  | 'missing_projected_completion'
  | 'no_parts_provided'
  | 'no_children_provided'
  | 'completed_work_would_be_reopened'
  | 'incomplete_work_would_be_hidden'
  | 'unresolved_child_would_be_marked_complete'
  | 'completed_child_would_be_marked_incomplete'
  | 'due_out_risk_not_recalculated'
  | 'running_timer_blocks_merge'
  | 'active_rollover_descendant_blocks_split'
  | 'picked_up_or_terminal_ticket'
  | 'waiter_flex_cannot_extend_due_out';

/**
 * Validation outcome. `ok === true` iff `blockers` is empty.
 *
 * `projectedFinalCompletion` is the ISO-string projection the validator
 * computed (when applicable). `overshootMin` is the minutes by which
 * projection exceeds Due Out (when the `final_completion_after_due_out`
 * blocker fires).
 */
export interface SplitMergeValidationResult {
  ok: boolean;
  blockers: SplitMergeBlocker[];
  projectedFinalCompletion?: string | null;
  overshootMin?: number;
}

/**
 * One part of a proposed split. `sequence` is 1-based.
 * `projectedFinish` is the caller's predicted opti-finish for that
 * part. `isCompleted` flags pre-completed parts (rare; defensive).
 */
export interface SplitPartInput {
  sequence: number;
  projectedFinish?: Date | string | null;
  isCompleted?: boolean;
}

/**
 * A child snapshot at merge time. Used by both manual-merge and
 * partial-completion-merge validators.
 */
export interface MergeChildSnapshot {
  ticketId: string;
  ticket?: ProductionStateTicketShape;
  projectedFinish?: Date | string | null;
  /** True iff this child has a running timer right now. */
  hasRunningTimer?: boolean;
  /** True iff this child's production work is complete. */
  isCompleted?: boolean;
}

// -----------------------------------------------------------------------------
// Split-creation input
// -----------------------------------------------------------------------------

export interface ValidateDueOutForSplitCreationInput {
  ticketId: string;
  /** Parent ticket's `due_out`. Required for any meaningful check. */
  dueOut: Date | string | null | undefined;
  /**
   * Optional full ticket row for production-state derivation. When
   * provided, the helper rejects splits on picked-up / deleted /
   * archived / completed_awaiting_pickup parents.
   */
  ticket?: ProductionStateTicketShape;
  /** Proposed split parts in sequence order. */
  parts: SplitPartInput[];
  /**
   * Caller-supplied: true iff the parent ticket has an ACTIVE rollover
   * descendant that would conflict with creating a fresh split. Driven
   * by Bucket 4 §4.3 doctrine — rollover descendants must be resolved
   * before re-splitting.
   */
  hasActiveRolloverDescendant?: boolean;
  /** Reference clock (for future use; currently unused by the helper). */
  now?: Date;
}

// -----------------------------------------------------------------------------
// Manual-merge input
// -----------------------------------------------------------------------------

export interface ValidateDueOutForManualMergeInput {
  /** The merge target ticket id (typically the primary/earliest sibling). */
  parentTicketId: string;
  /** The parent ticket's `due_out` to re-validate against. */
  dueOut: Date | string | null | undefined;
  children: MergeChildSnapshot[];
  /**
   * The caller's projected finish for the MERGED ticket (recomputed
   * from combined predicted minutes). The validator REQUIRES this to
   * be non-null — recomputing Due Out risk is the whole point of a
   * proactive merge gate. Omitting it emits
   * `due_out_risk_not_recalculated`.
   */
  mergedProjectedFinish?: Date | string | null;
  now?: Date;
}

// -----------------------------------------------------------------------------
// Partial-completion merge input
// -----------------------------------------------------------------------------

export interface ValidatePartialCompletionMergeDueOutInput
  extends ValidateDueOutForManualMergeInput {
  /**
   * Caller's plan: which sibling ids will land as COMPLETED in the
   * merged ticket's work record. The validator checks each against
   * the child's actual `isCompleted` to detect "incomplete work
   * silently marked complete".
   */
  plannedCompletedChildIds: string[];
  /**
   * Caller's plan: which sibling ids will land as INCOMPLETE /
   * remaining-work in the merged ticket. The validator checks each
   * against the child's actual `isCompleted` to detect "completed
   * work silently reopened".
   */
  plannedIncompleteChildIds: string[];
}

// =============================================================================
// Internal helpers
// =============================================================================

function parseTs(v: Date | string | null | undefined): number | null {
  if (v == null) return null;
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isFinite(t) ? t : null;
  }
  const t = Date.parse(String(v));
  return Number.isFinite(t) ? t : null;
}

function toIso(v: Date | string | null | undefined): string | null {
  const t = parseTs(v);
  return t == null ? null : new Date(t).toISOString();
}

/** Terminal / non-mutation production states that block split / merge. */
function isTerminalParent(ticket?: ProductionStateTicketShape): boolean {
  if (!ticket) return false;
  const state = getProductionState(ticket);
  return !canPerformActiveProductionMutation(ticket) ||
    state === 'picked_up' ||
    state === 'completed_awaiting_pickup' ||
    state === 'deleted' ||
    state === 'archived';
}

// =============================================================================
// Projection helpers
// =============================================================================

/**
 * Project the final downstream completion across an ordered set of
 * split parts. Returns the MAX `projectedFinish` (the latest part
 * dominates). Returns `null` if no part has a usable projectedFinish.
 *
 * Pure: depends only on input.
 */
export function projectSplitGroupFinalCompletion(
  parts: ReadonlyArray<SplitPartInput>,
): string | null {
  let maxMs = -Infinity;
  for (const p of parts) {
    const ms = parseTs(p.projectedFinish);
    if (ms != null && ms > maxMs) maxMs = ms;
  }
  return Number.isFinite(maxMs) ? new Date(maxMs).toISOString() : null;
}

// =============================================================================
// Validators
// =============================================================================

/**
 * Validate a proposed split creation against Due Out + lifecycle
 * preconditions. Bucket 4.5 §4.5.4: the final downstream completion
 * MUST be <= Due Out. Bucket 4 §4.3: rollover descendants block fresh
 * splits.
 *
 * Resolution order (errors are COLLECTED, not short-circuited):
 *   1. Terminal parent (picked_up / deleted / archived / completed
 *      awaiting pickup) → `picked_up_or_terminal_ticket`.
 *   2. Active rollover descendant → `active_rollover_descendant_blocks_split`.
 *   3. No parts → `no_parts_provided`.
 *   4. `dueOut` missing → `missing_due_out`.
 *   5. Any part lacks `projectedFinish` → `missing_projected_completion`.
 *   6. Projected final completion > dueOut → `final_completion_after_due_out`
 *      + populated `overshootMin`.
 *
 * Pure.
 */
export function validateDueOutForSplitCreation(
  input: ValidateDueOutForSplitCreationInput,
): SplitMergeValidationResult {
  const blockers: SplitMergeBlocker[] = [];

  if (isTerminalParent(input.ticket)) {
    blockers.push('picked_up_or_terminal_ticket');
  }
  if (input.hasActiveRolloverDescendant === true) {
    blockers.push('active_rollover_descendant_blocks_split');
  }
  if (!input.parts || input.parts.length === 0) {
    blockers.push('no_parts_provided');
  }

  const dueOutMs = parseTs(input.dueOut);
  if (dueOutMs == null) {
    blockers.push('missing_due_out');
  }

  let missingProjection = false;
  if (input.parts && input.parts.length > 0) {
    for (const p of input.parts) {
      if (parseTs(p.projectedFinish) == null) {
        missingProjection = true;
        break;
      }
    }
  }
  if (missingProjection) blockers.push('missing_projected_completion');

  const projectedFinalIso = input.parts
    ? projectSplitGroupFinalCompletion(input.parts)
    : null;
  let overshootMin: number | undefined;
  if (
    dueOutMs != null &&
    projectedFinalIso != null &&
    !missingProjection
  ) {
    const projectedMs = parseTs(projectedFinalIso) as number;
    if (projectedMs > dueOutMs) {
      blockers.push('final_completion_after_due_out');
      overshootMin = Math.round((projectedMs - dueOutMs) / 60_000);
    }
  }

  return {
    ok: blockers.length === 0,
    blockers,
    projectedFinalCompletion: projectedFinalIso,
    overshootMin,
  };
}

/**
 * Validate a proposed manual merge. The caller MUST supply a
 * `mergedProjectedFinish` recomputed from combined predicted minutes —
 * the whole point of the gate is to re-evaluate Due Out, not trust
 * pre-split state.
 *
 * Resolution order (collected):
 *   1. No children → `no_children_provided`.
 *   2. Terminal parent / picked-up child → `picked_up_or_terminal_ticket`.
 *   3. Any child has a running timer → `running_timer_blocks_merge`.
 *   4. `dueOut` missing → `missing_due_out`.
 *   5. `mergedProjectedFinish` missing → `due_out_risk_not_recalculated`.
 *   6. `mergedProjectedFinish > dueOut` → `final_completion_after_due_out`
 *      + populated `overshootMin`.
 *
 * Pure.
 */
export function validateDueOutForManualMerge(
  input: ValidateDueOutForManualMergeInput,
): SplitMergeValidationResult {
  const blockers: SplitMergeBlocker[] = [];

  if (!input.children || input.children.length === 0) {
    blockers.push('no_children_provided');
  }

  // Terminal-state check across children.
  let anyTerminal = false;
  let anyRunningTimer = false;
  if (input.children) {
    for (const c of input.children) {
      if (c.ticket && isTerminalParent(c.ticket)) anyTerminal = true;
      if (c.hasRunningTimer === true) anyRunningTimer = true;
    }
  }
  if (anyTerminal) blockers.push('picked_up_or_terminal_ticket');
  if (anyRunningTimer) blockers.push('running_timer_blocks_merge');

  const dueOutMs = parseTs(input.dueOut);
  if (dueOutMs == null) blockers.push('missing_due_out');

  const mergedFinishMs = parseTs(input.mergedProjectedFinish);
  if (mergedFinishMs == null) {
    blockers.push('due_out_risk_not_recalculated');
  }

  let overshootMin: number | undefined;
  if (dueOutMs != null && mergedFinishMs != null && mergedFinishMs > dueOutMs) {
    blockers.push('final_completion_after_due_out');
    overshootMin = Math.round((mergedFinishMs - dueOutMs) / 60_000);
  }

  return {
    ok: blockers.length === 0,
    blockers,
    projectedFinalCompletion: toIso(input.mergedProjectedFinish),
    overshootMin,
  };
}

/**
 * Validate a proposed partial-completion merge. Adds the
 * preservation-of-truth checks on top of the manual-merge gate:
 *   - A completed child planned as incomplete → `completed_child_would_be_marked_incomplete`
 *     and `completed_work_would_be_reopened`.
 *   - An incomplete child planned as completed → `unresolved_child_would_be_marked_complete`
 *     and `incomplete_work_would_be_hidden`.
 *
 * Bucket 4 doctrine: split/merge must preserve audit/history truth.
 * Completed work must remain completed; incomplete work must remain
 * visible.
 *
 * Pure.
 */
export function validatePartialCompletionMergeDueOut(
  input: ValidatePartialCompletionMergeDueOutInput,
): SplitMergeValidationResult {
  const baseResult = validateDueOutForManualMerge(input);
  const blockers = [...baseResult.blockers];

  const completedSet = new Set(input.plannedCompletedChildIds ?? []);
  const incompleteSet = new Set(input.plannedIncompleteChildIds ?? []);

  let completedReopened = false;
  let incompleteHidden = false;

  for (const c of input.children ?? []) {
    if (c.isCompleted === true && incompleteSet.has(c.ticketId)) {
      completedReopened = true;
    }
    if (c.isCompleted === false && completedSet.has(c.ticketId)) {
      incompleteHidden = true;
    }
  }

  if (completedReopened) {
    blockers.push('completed_child_would_be_marked_incomplete');
    blockers.push('completed_work_would_be_reopened');
  }
  if (incompleteHidden) {
    blockers.push('unresolved_child_would_be_marked_complete');
    blockers.push('incomplete_work_would_be_hidden');
  }

  return {
    ok: blockers.length === 0,
    blockers,
    projectedFinalCompletion: baseResult.projectedFinalCompletion,
    overshootMin: baseResult.overshootMin,
  };
}

// =============================================================================
// Convenience wrappers
// =============================================================================

/** True iff the proposed split passes every Due Out / lifecycle gate. */
export function canSplitWithoutDueOutViolation(
  input: ValidateDueOutForSplitCreationInput,
): boolean {
  return validateDueOutForSplitCreation(input).ok;
}

/**
 * True iff the proposed merge passes every Due Out + preservation
 * gate. Accepts either a manual-merge input or a partial-completion
 * merge input.
 */
export function canMergeWithoutHidingDueOutRisk(
  input:
    | ValidateDueOutForManualMergeInput
    | ValidatePartialCompletionMergeDueOutInput,
): boolean {
  if (
    'plannedCompletedChildIds' in input ||
    'plannedIncompleteChildIds' in input
  ) {
    return validatePartialCompletionMergeDueOut(
      input as ValidatePartialCompletionMergeDueOutInput,
    ).ok;
  }
  return validateDueOutForManualMerge(input).ok;
}

/**
 * Single-shot blocker collector for any of the three validators.
 * Dispatches based on the shape of the input object.
 */
export function getSplitMergeDueOutBlockers(
  input:
    | ValidateDueOutForSplitCreationInput
    | ValidateDueOutForManualMergeInput
    | ValidatePartialCompletionMergeDueOutInput,
): SplitMergeBlocker[] {
  if ('parts' in input) {
    return validateDueOutForSplitCreation(input).blockers;
  }
  if (
    'plannedCompletedChildIds' in input ||
    'plannedIncompleteChildIds' in input
  ) {
    return validatePartialCompletionMergeDueOut(
      input as ValidatePartialCompletionMergeDueOutInput,
    ).blockers;
  }
  return validateDueOutForManualMerge(input as ValidateDueOutForManualMergeInput).blockers;
}

// =============================================================================
// Adoption points (documented in code; no behavior change in this slice)
// =============================================================================
//
// 1. execute-manual-split (smallest next step):
//    Replace the per-part throw at L465-472 with a single pre-flight
//    call to `validateDueOutForSplitCreation({ ticketId, dueOut, ticket,
//    parts: <all sequenced parts>, hasActiveRolloverDescendant })`.
//    Throw / return early on `!ok` with the collected blockers in the
//    error message. Net behavior change: covers the same "miss"
//    condition the existing throw catches, PLUS the rollover-descendant
//    and terminal-parent gates the throw misses.
//
// 2. split-ticket (smallest next step):
//    Add a single pre-flight `validateDueOutForSplitCreation` call
//    before any DB write. The current code path inherits due_out
//    without checking it; adopting this gate matches execute-manual-
//    split's coverage.
//
// 3. ticketMerging.mergeSplitTickets:
//    Before invoking smart-assign with combined jobs, call
//    `validateDueOutForManualMerge({ parentTicketId, dueOut, children,
//    mergedProjectedFinish })` where `mergedProjectedFinish` is
//    recomputed from the combined predicted minutes against the
//    primary tech's lane. On `!ok`, surface a toast with the blockers
//    and abort the merge.
//
// 4. Partial-completion merge (future code path):
//    When a partial-completion merge UI lands, call
//    `validatePartialCompletionMergeDueOut(...)` to gate the truth-
//    preservation rules. The validator already emits the four discrete
//    blockers a UI needs (completed_work_would_be_reopened /
//    incomplete_work_would_be_hidden + the explicit
//    *_would_be_marked_* pair).
//
// 5. Telemetry:
//    Log `result.blockers` distribution per attempted split/merge so
//    we measure how often each blocker fires before any auto-resolve
//    behavior is enabled.
//
// 6. Re-promise on failure:
//    A future Catalyst path can chain a failed split/merge into a
//    Phase 5.1 `buildDueOutEditAuditPayload({...intent: 'breach_record'})`
//    routed through `mutateTicketSchedule.dueOutEditAuditContext` so
//    the breach is audit-logged when no safe split/merge plan exists.
//    Bucket 4.5 §4.5.9: Waiter Flex still does NOT extend Due Out on
//    this path.
